import React from 'react';
import {
  clearAuth,
  deletePayment,
  selectUser,
  terminateUser,
  withTextLocalizer,
  forgetPassword,
  selectRentalData,
  removeCircle,
  LocalizePropType,
  selectCircleId,
  isKYCApp,
  selectBrandSettings,
  setIsVerifyingGDPREmail,
  isMVV,
  BrandData,
  getAddressStatus,
  getEmailVerificationStatus,
  getIDStatus,
  getLicenseStatus,
  getPhoneStatus,
  getTariffStatus,
  isEmailVerificationRequired,
  isIDVerificationRequired,
  isIncompleteProfile,
  isTariffOpen,
  isTariffRequired,
  getNextUserOpenChallengeRoute,
  getPinStatus,
  selectUserTransactions,
  isV2BankFlow,
  getDepositStatus,
  setNotification,
  APP,
  UserDataOutput,
} from 'getaway-data-layer';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import AccountTabView from './AccountTabView';
import {
  GA_TITLES,
  isMobileBrowser,
  navigateTo,
  reportGAEvent,
  showAlert,
  showAlertWithOptions,
} from '../../../../../utils';
import { useCustomerSupport } from '../../../../../hooks/useCustomerSupport';
import { setIsEditingPhone } from 'getaway-data-layer/src/redux/store/ui/firstSteps/Actions';
import { showAboutDialog } from '../../../../../utils/dialogsHelpers';
import config from '../../../../../env.config';
import packageInfo from '../../../../../../package.json';

type Props = {
  localize: LocalizePropType;
  migratedBubUser: UserDataOutput | null;
  isDeUbqShutdown: boolean;
};

const AccountTabContainer = ({
  localize,
  migratedBubUser,
  isDeUbqShutdown,
}: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { helpAction } = useCustomerSupport(localize);
  const user = useSelector(selectUser);
  const brandSettings = useSelector(selectBrandSettings) as BrandData;
  const selectedCircleId = useSelector(selectCircleId);
  const isServiceHero =
    user?.serviceHero ||
    !!user?.circles?.find(
      (circle) =>
        circle.id === selectedCircleId && circle?.circleType === 'service'
    );
  const inCompleteProfile = isIncompleteProfile(user);
  let onboardingOnlyProduct = isKYCApp();

  const hasSyncedMerchant = user?.merchantSystemData?.find(
    (merchant) =>
      (merchant?.status === 'DONE' && merchant?.syncedAt) ||
      (merchant?.status === 'IMPORTED' && merchant?.merchantReference)
  );
  // For imported users, we consider readOnlyMode if they have been synced
  const readOnlyMode =
    ((!inCompleteProfile || hasSyncedMerchant) &&
      onboardingOnlyProduct &&
      !!(isMVV() ? user?.extraData : true)) ||
    !!migratedBubUser ||
    isDeUbqShutdown;
  // For imported users, synced but incomplete
  const inCompleteReadOnlyMode =
    (readOnlyMode && inCompleteProfile) || !!migratedBubUser || isDeUbqShutdown;

  const rentalData = useSelector(selectRentalData) as any;

  const phoneStatus = getPhoneStatus(user);
  const addressStatus = getAddressStatus(user);
  const licenseStatus = getLicenseStatus(user);
  const idStatus = getIDStatus(user);
  const tariffStatus = getTariffStatus(user);
  const emailStatus = getEmailVerificationStatus(user);
  const pinStatus = getPinStatus(user);

  const transactions = useSelector(selectUserTransactions);
  const openTransactions =
    transactions?.transactions?.filter(
      (transaction) => transaction.status === 'open'
    ) || [];

  const hasPayment =
    user?.paymentMethods?.length &&
    user?.paymentMethods?.find((payment: any) => payment.status === 'verified');

  const isDocumentsAfterDeposit =
    (!hasPayment ||
      (user?.deposit?.balance === 0 && getDepositStatus(user) === 'open')) &&
    user?.deposit?.toPayForSepaUnlock >
      // @ts-ignore
      brandSettings?.kycSettings?.documentsAfterDepositMinAmount;

  const isCWRequiredForDocumentVerification =
    user?.userType === 'user' &&
    isKYCApp() &&
    brandSettings?.cwRequiredForDocumentVerification &&
    user?.schufaCheckRequired;

  const onPressPhoneIncomplete = () => {
    navigateTo(history, '/dashboard/addPhone');
  };

  const showHelpDialog = () => {
    showAlert(
      '',
      localize('user.phone.completed.message'),
      localize('user.action.support.text'),
      () => helpAction(),
      localize('user.notNow'),
      () => {}
    );
  };

  const onPressPhoneCompleted = () => {
    showAlert(
      '',
      localize('user.phone.edit.desc'),
      localize('continue'),
      () => {
        dispatch(setIsEditingPhone(true));
        navigateTo(history, '/dashboard/email');
      },
      localize('cancel'),
      () => {}
    );
  };

  const onPressAddress = () => {
    navigateTo(history, '/dashboard/addAddress');
  };

  const onPressAddressInProgress = () => {
    showAlert(
      '',
      localize('user.address.inProgress.message'),
      null,
      () => {},
      localize('ok'),
      () => {}
    );
  };

  const onPressAddressCompleted = () => {
    if (isIDVerificationRequired(user)) {
      showAlert(
        '',
        localize('user.ID.completed.message'),
        localize('user.action.support.text'),
        () => setTimeout(() => helpAction(), 100),
        localize('user.notNow'),
        () => {}
      );
    } else {
      onPressAddress();
    }
  };

  const showTariffRequiredDialog = () => {
    showAlert(
      '',
      localize('api_error.user.tariff_missing'),
      localize('dialog.tariff.add'),
      onPressTariff,
      localize('cancel'),
      () => {}
    );
  };

  const showDepositRequiredDialog = () => {
    if (hasPayment) {
      showAlert(
        '',
        localize('profile.deposit.missing'),
        localize('dialog.deposit.add'),
        onAddDepositPayment,
        localize('cancel'),
        () => {}
      );
    } else {
      showAlert(
        '',
        localize('profile.payment.missing'),
        localize('dialog.payment.add'),
        onAddPayment,
        localize('cancel'),
        () => {}
      );
    }
  };

  const showSchufaRequiredDialog = () => {
    showAlert(
      '',
      localize('profile.schufa.missing'),
      localize('dialog.schufa.add'),
      onAddSchufa,
      localize('cancel'),
      () => {}
    );
  };

  const onPressDrivingLicense = () => {
    if (isDocumentsAfterDeposit) {
      showDepositRequiredDialog();
    } else if (isTariffRequired(user) && isTariffOpen(user)) {
      showTariffRequiredDialog();
    } else if (
      brandSettings?.paymentRequiredForDLVerification &&
      !user?.paymentMethods?.find(
        (payment: any) => payment.status === 'verified'
      ) &&
      !isServiceHero
    ) {
      if (user?.deposit?.toPayForSepaUnlock !== undefined && !user?.blocked) {
        showAlert(
          '',
          localize('user.noPayment.message'),
          localize('user.noPayment.action'),
          () => onAddDepositPayment(),
          localize('cancel'),
          () => {}
        );
      } else {
        showAlert(
          '',
          localize('user.noPayment.message'),
          localize('user.noPayment.action'),
          () => onAddPayment(),
          localize('cancel'),
          () => {}
        );
      }
    } else if (isCWRequiredForDocumentVerification) {
      showSchufaRequiredDialog();
    } else {
      if (
        !user?.remainingLicenseSubmissionAttempts ||
        user?.remainingLicenseSubmissionAttempts > 0
      ) {
        navigateTo(history, '/dashboard/DLOnboarding');
      } else {
        showAlert(
          '',
          localize('user.DL.resubmission.message'),
          localize('user.action.support.text'),
          () => showHelpDialog(),
          localize('user.notNow'),
          () => {}
        );
      }
    }
  };

  const onPressDLInProgress = () => {
    showAlert(
      '',
      localize('user.DL.inProgress.message'),
      null,
      () => {},
      localize('ok'),
      () => {}
    );
  };

  const onPressDLCompleted = () => {
    showAlert(
      '',
      localize('user.DL.completed.message'),
      localize('user.action.support.text'),
      () => setTimeout(() => helpAction(), 100),
      localize('user.notNow'),
      () => {}
    );
  };

  const onPressID = () => {
    if (isDocumentsAfterDeposit) {
      showDepositRequiredDialog();
    } else if (isTariffRequired(user) && isTariffOpen(user)) {
      showTariffRequiredDialog();
    } else if (
      brandSettings?.paymentRequiredForIDVerification &&
      !user?.paymentMethods?.find(
        (payment: any) => payment.status === 'verified'
      ) &&
      !isServiceHero
    ) {
      if (
        user?.deposit?.toPayForSepaUnlock !== undefined &&
        !user?.blocked &&
        user?.paymentMethods?.[0]?.sepa?.iban
      ) {
        showAlert(
          '',
          localize('user.noPayment.message'),
          localize('user.noPayment.action'),
          () => onAddDepositPayment(),
          localize('cancel'),
          () => {}
        );
      } else {
        showAlert(
          '',
          localize('user.noPayment.message'),
          localize('user.noPayment.action'),
          () => onAddPayment(),
          localize('cancel'),
          () => {}
        );
      }
    } else if (isCWRequiredForDocumentVerification) {
      showSchufaRequiredDialog();
    } else {
      if (
        !user?.remainingIDSubmissionAttempts ||
        user?.remainingIDSubmissionAttempts > 0
      ) {
        navigateTo(history, '/dashboard/IDOnboarding');
      } else {
        showAlert(
          '',
          localize('user.ID.resubmission.message'),
          localize('user.action.support.text'),
          () => showHelpDialog(),
          localize('user.notNow'),
          () => {}
        );
      }
    }
  };

  const onPressIDInProgress = () => {
    showAlert(
      '',
      localize('user.ID.inProgress.message'),
      null,
      () => {},
      localize('ok'),
      () => {}
    );
  };

  const onPressIDCompleted = () => {
    showAlert(
      '',
      localize('user.ID.completed.message'),
      localize('user.action.support.text'),
      () => setTimeout(() => helpAction(), 100),
      localize('user.notNow'),
      () => {}
    );
  };

  const onDeletePayment = (payment) => {
    if (['blocked', 'pending']?.includes(payment?.status)) {
      showAlert(
        '',
        localize(
          user?.userType === 'org'
            ? 'user.profile.payment.org.pending'
            : 'user.profile.payment.pending'
        ),
        '',
        () => {},
        localize('ok'),
        () => {}
      );
    } else if (rentalData?.carMode) {
      showAlert(
        '',
        localize('user.logout.cancelReservation.message'),
        '',
        () => {},
        localize('user.logout.cancelReservation.ok'),
        () => {}
      );
    } else {
      showAlert(
        localize('user.deletePayment.dialog.title'),
        localize('user.deletePayment.dialog.message'),
        localize('user.deletePayment.dialog.yes'),
        () => dispatch(deletePayment(payment.id)),
        localize('user.deletePayment.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onOpenPaymentProfile = (payment) => {
    const isOrg = user?.userType === 'org';
    if (isOrg || user?.address?.billingProfile === 'business') {
      navigateTo(history, `/dashboard/optimizedSepa/${payment.id}`);
    } else if (['blocked', 'pending']?.includes(payment?.status)) {
      showAlert(
        '',
        localize(
          user?.userType === 'org'
            ? 'user.profile.payment.org.pending'
            : 'user.profile.payment.pending'
        ),
        '',
        () => {},
        localize('ok'),
        () => {}
      );
    } else {
      navigateTo(history, `/dashboard/paymentProfile/${payment.id}`);
    }
  };

  const onPressEmail = () => {
    if (
      isEmailVerificationRequired(user) &&
      getEmailVerificationStatus(user) !== 'completed'
    ) {
      navigateTo(history, '/dashboard/email');
    } else {
      showAlert(
        '',
        localize('user.email.completed.message'),
        localize('user.action.support.text'),
        () => setTimeout(() => helpAction(), 100),
        localize('user.notNow'),
        () => {}
      );
    }
  };

  const onPressPin = () => {
    if (!readOnlyMode) {
      navigateTo(history, '/dashboard/addPin');
    } else {
      showAlert(
        '',
        localize('user.pin.completed.message'),
        localize('user.action.support.text'),
        () => setTimeout(() => helpAction(), 100),
        localize('user.notNow'),
        () => {}
      );
    }
  };

  const onPressPassword = () => {
    dispatch(forgetPassword(user?.email!!, null, null, localize));
  };

  const onLogout = () => {
    if (rentalData?.carMode) {
      showAlert(
        '',
        localize('user.logout.cancelReservation.message'),
        '',
        () => {},
        localize('user.logout.cancelReservation.ok'),
        () => {}
      );
    } else {
      showAlert(
        localize('user.logout.dialog.title'),
        localize('user.logout.dialog.message'),
        localize('user.logout.dialog.yes'),
        () => {
          localStorage?.clear();
          dispatch(clearAuth());
          navigateTo(history, onboardingOnlyProduct ? '/' : '/map');
          reportGAEvent(GA_TITLES.AUTH.LOGOUT_SUBMITTED);
        },
        localize('user.logout.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onTerminateUser = () => {
    if (rentalData?.carMode) {
      showAlert(
        '',
        localize('user.logout.cancelReservation.message'),
        '',
        () => {},
        localize('user.logout.cancelReservation.ok'),
        () => {}
      );
    } else {
      showAlert(
        localize('user.terminate.dialog.title'),
        localize('user.terminate.dialog.message'),
        localize('user.terminate.dialog.yes'),
        () => {
          dispatch(
            terminateUser(() => {
              navigateTo(history, onboardingOnlyProduct ? '/' : '/map');
              dispatch(
                setNotification({
                  message: `${localize('user.notification.terminate.message')}`,
                  type: APP.NOTIFICATION_TYPE.ERROR,
                })
              );
            })
          );
        },
        localize('user.terminate.dialog.no'),
        () => {},
        true
      );
    }
  };

  const onAddPayment = () => {
    if (
      brandSettings?.kycSettings?.tariffSelectionRequiredForPayment &&
      isTariffRequired(user) &&
      isTariffOpen(user)
    ) {
      showTariffRequiredDialog();
    } else if (isKYCApp()) {
      if (
        brandSettings?.trueLayerSettings?.bankLoginEnabled ||
        brandSettings?.kycSettings?.separateCreditCheckScreen
      ) {
        if (!user?.dateOfBirth) {
          navigateTo(history, '/dashboard/dob?next=schufa');
        } else if (user?.schufaCheckRequired) {
          navigateTo(history, '/dashboard/schufa');
        } else {
          if (
            brandSettings?.trueLayerSettings?.bankLoginEnabled &&
            !isV2BankFlow()
          ) {
            navigateTo(history, '/dashboard/selectBank');
          } else {
            navigateTo(history, '/dashboard/optimizedSepa');
          }
        }
      } else {
        navigateTo(history, '/dashboard/optimizedSepa');
      }
    } else {
      navigateTo(history, '/dashboard/selectPayment');
    }
  };

  const onClose = () => {
    navigateTo(history, isMobileBrowser() ? '/map' : '/');
  };

  const onPressConsents = () => {
    navigateTo(history, '/dashboard/consents');
  };

  const onPressCircle = (circle: any) => {
    showAlert(
      '',
      localize('user.circle.description'),
      localize('user.circle.leave'),
      () =>
        setTimeout(
          () =>
            showAlert(
              '',
              localize('user.circle.leave.description'),
              localize('user.circle.leave'),
              () => dispatch(removeCircle(circle.circleCode)),
              localize('cancel'),
              () => {},
              true
            ),
          100
        ),
      localize('cancel'),
      () => {},
      true
    );
  };

  const onAddDepositPayment = () => {
    navigateTo(history, '/dashboard/deposit');
  };

  const onAddSchufa = () => {
    navigateTo(history, '/dashboard/schufa');
  };

  const onDepositBalanceClick = () => {
    if (user?.deposit?.invoiceUrl) {
      const options = [
        {
          title: localize('deposit.dialog.receipt'),
          action: () => {
            window.open(user?.deposit?.invoiceUrl, '_blank');
          },
        },
      ];
      showAlertWithOptions(localize('deposit.dialog.title'), '', options);
    } else {
      showAlert('', localize('user.deposit.dialog.msg'), localize('ok'));
    }
  };

  const onPressGDPR = () => {
    const options = [
      {
        title: localize('profile.gdpr.dialog.request'),
        action: () => {
          dispatch(setIsVerifyingGDPREmail(true));
          navigateTo(history, '/dashboard/email');
        },
      },
      {
        title: localize('profile.gdpr.dialog.more'),
        action: () => {
          window.open(localize('profile.gdpr.dialog.more.url'), '_blank');
        },
      },
      { title: localize('cancel') },
    ];

    showAlertWithOptions(
      localize('profile.gdpr.dialog.title'),
      localize(
        'profile.gdpr.dialog.msg',
        brandSettings?.name,
        brandSettings?.merchantName
      ),
      options
    );
  };

  const onPressJoinCircle = () => {
    navigateTo(history, '/dashboard/joinCircle');
  };

  const onPressTariff = () => {
    if (user?.tariff?.partnerSubAccountEligible) {
      return;
    }
    if (
      (brandSettings?.trueLayerSettings?.bankLoginEnabled ||
        brandSettings?.kycSettings?.separateCreditCheckScreen) &&
      !user?.dateOfBirth
    ) {
      navigateTo(history, `/dashboard/dob`);
    } else {
      navigateTo(history, `/dashboard/tariff`);
    }
  };

  const onPressTariffCompleted = () => {
    if (isIncompleteProfile(user)) {
      onPressTariff();
    } else {
      showAlert(
        '',
        localize('user.ID.completed.message'),
        localize('user.action.support.text'),
        () => setTimeout(() => helpAction(), 100),
        localize('user.notNow'),
        () => {}
      );
    }
  };

  const onAddPaymentProfile = () => {
    if (
      brandSettings?.kycSettings?.tariffSelectionRequiredForPayment &&
      isTariffRequired(user) &&
      isTariffOpen(user)
    ) {
      showTariffRequiredDialog();
    } else {
      navigateTo(history, `/dashboard/paymentProfile`);
    }
  };

  const openOnboardingWizard = () => {
    navigateTo(history, getNextUserOpenChallengeRoute(user, '', 'onboarding'));
  };

  const onClickVersionHeader = () => {
    showAboutDialog(
      config.brand.xBrandId!!,
      localize,
      `© GETAWAY・v${packageInfo.version}`
    );
  };

  return (
    <AccountTabView
      user={user!!}
      localize={localize}
      readOnlyMode={readOnlyMode}
      inCompleteReadOnlyMode={inCompleteReadOnlyMode}
      phoneNumber={user?.phoneNumber!!}
      phoneStatus={phoneStatus}
      blocked={user?.blocked}
      onPressPhoneIncomplete={onPressPhoneIncomplete}
      onPressPhoneCompleted={onPressPhoneCompleted}
      address={user?.address}
      addressStatus={addressStatus}
      tariffStatus={tariffStatus}
      emailStatus={emailStatus}
      pinStatus={pinStatus}
      onPressAddress={onPressAddress}
      onPressAddressInProgress={onPressAddressInProgress}
      onPressAddressCompleted={onPressAddressCompleted}
      license={user?.license}
      licenseStatus={licenseStatus}
      idStatus={idStatus}
      onPressDrivingLicense={onPressDrivingLicense}
      onPressDLInProgress={onPressDLInProgress}
      onPressDLCompleted={onPressDLCompleted}
      onOpenPaymentProfile={onOpenPaymentProfile}
      onPressEmail={onPressEmail}
      onPressPin={onPressPin}
      onPressPassword={onPressPassword}
      onTerminateUser={onTerminateUser}
      onClose={onClose}
      onLogout={onLogout}
      onAddPayment={onAddPayment}
      onPressCircle={onPressCircle}
      onPressJoinCircle={onPressJoinCircle}
      onPressID={onPressID}
      onPressIDInProgress={onPressIDInProgress}
      onPressIDCompleted={onPressIDCompleted}
      onboardingOnly={onboardingOnlyProduct}
      onDeletePayment={onDeletePayment}
      onAddPaymentProfile={onAddPaymentProfile}
      onAddDepositPayment={onAddDepositPayment}
      onDepositBalanceClick={onDepositBalanceClick}
      onPressGDPR={onPressGDPR}
      onPressTariff={onPressTariff}
      onPressTariffCompleted={onPressTariffCompleted}
      onPressConsents={onPressConsents}
      onClickVersionHeader={onClickVersionHeader}
      openOnboardingWizard={openOnboardingWizard}
      v2OrgSignupEnabled={brandSettings?.kycSettings?.v2OrgSignupEnabled}
      separateCreditCheckScreen={
        brandSettings?.kycSettings?.separateCreditCheckScreen
      }
      migratedBubUser={migratedBubUser}
      isDeUbqShutdown={isDeUbqShutdown}
    />
  );
};

export default withTextLocalizer(AccountTabContainer);
